import {
  type ClipboardEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import { SmsCodeIcon } from "@hugeicons-pro/core-solid-rounded";
import { Button, ButtonVariant, Modal } from "shared/components";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";

const OTP_LENGTH = 6;

// The prompt is raised from inside a request, so whatever modal started that request is
// still on screen underneath it. Every one of those hosts (regenerate secret, connect
// bank, change password) uses the default z-index of 50, so outrank them explicitly
// rather than relying on this modal happening to render later in the DOM.
const STEP_UP_Z_INDEX = 200;

interface ReAuthModalProps {
  opened: boolean;
  onClose: () => void;
  // Confirming stamps the pass on the login session itself (BF-409), so there is no
  // token for the caller to carry to the guarded request.
  onConfirm: () => void;
}

export const ReAuthModal = ({
  opened,
  onClose,
  onConfirm
}: ReAuthModalProps) => {
  const { auth } = useAuthContext();
  const [digits, setDigits] = useState<string[]>(
    Array(OTP_LENGTH).fill("") as string[]
  );

  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const otpCode = digits.join("");

  const { mutate: sendOtp, isPending: isSendingOtp } = useMutation({
    mutationFn: () =>
      // skipStepUpAuth: this is the prompt itself. Were it ever refused for a missing
      // pass, the interceptor would wait on the prompt already on screen.
      instance.post("/portal/v1/auth/otp/send", undefined, {
        skipStepUpAuth: true
      }),
    onError: (err) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      toast.error(
        status === 429
          ? "Please wait a minute before requesting another code."
          : "Failed to send verification code. Please try again."
      );
    }
  });

  useEffect(() => {
    if (!opened) return;
    setDigits(Array(OTP_LENGTH).fill("") as string[]);
    sendOtp();
  }, [opened]);

  const { mutate: verifyReauth, isPending: isVerifying } = useMutation({
    mutationFn: async (body: {
      otp_code: string;
    }): Promise<{ reauth_token: string }> => {
      // skipAuthRefresh prevents the JWT interceptor from treating a 422 wrong-credential
      // response as a session expiry and attempting a token refresh.
      // skipStepUpAuth for the same reason as the send call above.
      const options = { skipAuthRefresh: true, skipStepUpAuth: true };
      const { data } = await instance.post<{ reauth_token: string }>(
        "/portal/v1/auth/otp/verify",
        body,
        options
      );
      return data;
    },
    onSuccess: () => onConfirm(),
    onError: (err) => {
      const message = (
        err as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      toast.error(message || "Verification failed. Please try again.");
    }
  });

  const handleClose = () => {
    setDigits(Array(OTP_LENGTH).fill("") as string[]);
    onClose();
  };

  const handleConfirm = () => {
    verifyReauth({ otp_code: otpCode });
  };

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) digitRefs.current[index + 1]?.focus();
  };

  const handleDigitKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0)
      digitRefs.current[index - 1]?.focus();
  };

  const handleDigitPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("") as string[];
    pasted.split("").forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    digitRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const isConfirmDisabled =
    isVerifying || otpCode.length < OTP_LENGTH || isSendingOtp;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      zIndex={STEP_UP_Z_INDEX}
      panelClassName="max-w-[366px] w-full p-6"
    >
      <div className="flex flex-col items-center text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-20">
          <HugeiconsIcon
            icon={SmsCodeIcon}
            size={32}
            color="var(--primary-500)"
          />
        </div>

        <h1 className="text-2xl font-bold text-content-1000 mb-4">
          Enter the 6-digit code we&apos;ve sent to
        </h1>
        <p className="text-sm text-content-400 mb-6">{auth?.email}</p>

        <div className="flex w-full gap-2 mb-4">
          {Array.from({ length: OTP_LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => {
                digitRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleDigitKeyDown(i, e)}
              onPaste={i === 0 ? handleDigitPaste : undefined}
              autoFocus={i === 0}
              className="h-12 flex-1 min-w-0 rounded-lg border border-alpha-200 bg-white text-center text-lg font-semibold text-content-1000 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => sendOtp()}
          disabled={isSendingOtp}
          className="text-sm text-primary-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSendingOtp ? "Sending…" : "Resend code"}
        </button>
      </div>

      <div className="flex gap-2 mt-6">
        <Button className="w-full" onClick={handleClose} disabled={isVerifying}>
          Cancel
        </Button>
        <Button
          className="w-full"
          variant={ButtonVariant.PRIMARY}
          onClick={handleConfirm}
          loading={isVerifying}
          disabled={isConfirmDisabled}
        >
          Modify settings
        </Button>
      </div>
    </Modal>
  );
};
